import React, { useEffect, useState, useRef } from "react";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import UISwitch from "../../components/UISwitch";
import { useLanguage } from "../../context/LanguageContext";
import {
  Settings, Building2, Bell, Save, Radio, Clock,
  Palette, Tag, Plus, X,
} from "lucide-react";


// Safe save — creates doc if it doesn't exist yet
async function saveDoc(ref, data) {
  try { await updateDoc(ref, data); }
  catch (e) {
    if (e.code === "not-found") await setDoc(ref, data);
    else throw e;
  }
}

function CompanySettings({ companyId }) {
  const { t, setLang } = useLanguage();
  const [settings, setSettings]   = useState(null);
  const [profile,  setProfile]    = useState(null);
  const [loading,  setLoading]    = useState(true);
  const [saving,   setSaving]     = useState(false);
  const [error,    setError]      = useState("");
  const [success,  setSuccess]    = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [newTypeName, setNewTypeName] = useState("");

  // Logo upload
  const [logoFile,     setLogoFile]     = useState(null);
  const [logoPreview,  setLogoPreview]  = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [settingsSnap, profileSnap, rootSnap] = await Promise.all([
        getDoc(doc(db, "companies", companyId, "config", "settings")),
        getDoc(doc(db, "companies", companyId, "profile", "info")),
        getDoc(doc(db, "companies", companyId)),
      ]);

      // Default settings if not exist
      setSettings(settingsSnap.exists() ? settingsSnap.data() : {
        timezone:               "Asia/Dubai",
        language:               "en",
        date_format:            "DD/MM/YYYY",
        nearby_distance_meters: 30,
        nearby_min:             20,
        nearby_max:             100,
        visit_reset_days:       1,
        auto_checkin:           true,
        notification_sound:     true,
        working_hours_start:    "08:00",
        working_hours_end:      "23:00",
        max_speed_kmh:          0,
        out_of_zone_alerts:          true,
        unauthorized_zone_alerts:    true,
        alert_cooldown_minutes:      15,
        track_outside_hours:    false,
        require_notes_on_visit: false,
        allow_manual_checkin:   true,
        client_types:           ["Residential", "Commercial", "Government", "Other"],
      });

      // Default profile if not exist — fall back to root doc logo_url if profile/info has none
      const rootLogoUrl = rootSnap.exists() ? (rootSnap.data().logo_url || "") : "";
      const profileData = profileSnap.exists() ? profileSnap.data() : {
        name: "", phone: "", email: "", address: "", logo_url: "", primary_color: "#16a34a",
      };
      if (!profileData.logo_url && rootLogoUrl) profileData.logo_url = rootLogoUrl;
      setProfile(profileData);
    } catch (err) {
      setError("Failed to load settings: " + err.message);
    }
    setLoading(false);
  }

  function flash(msg, isError = false) {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 4000);
  }

  // ── Logo select ────────────────────────────────────────────────────────────
  function handleLogoSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { flash(t("cs_logo_type_err"), true); return; }
    if (file.size > 2 * 1024 * 1024)    { flash(t("cs_logo_size_err"), true); return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  }

  // ── Upload logo to Firebase Storage ───────────────────────────────────────
  async function uploadLogo() {
    if (!logoFile) return profile?.logo_url || "";
    setUploadingLogo(true);
    try {
      const storageRef = ref(storage, `companies/${companyId}/logo`);
      await uploadBytes(storageRef, logoFile);
      const url = await getDownloadURL(storageRef);
      return url;
    } finally {
      setUploadingLogo(false);
    }
  }

  // ── Save all settings ──────────────────────────────────────────────────────
  async function handleSave() {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Upload logo first if changed
      const logoUrl = await uploadLogo();
      const updatedProfile = { ...profile, logo_url: logoUrl };

      await Promise.all([
        saveDoc(doc(db, "companies", companyId, "config", "settings"), settings),
        saveDoc(doc(db, "companies", companyId, "profile", "info"),    updatedProfile),
        // Save vendor-facing fields to company root so VendorDashboard can read them
        updateDoc(doc(db, "companies", companyId), {
          nearby_min:             settings.nearby_min             ?? 20,
          nearby_max:             settings.nearby_max             ?? 100,
          visit_reset_days:       settings.visit_reset_days       ?? 1,
          allow_manual_checkin:   settings.allow_manual_checkin   !== false,
          require_notes_on_visit: settings.require_notes_on_visit === true,
          logo_url:               logoUrl || "",
        }).catch(() => {}), // non-blocking — admin may not have write access to root
      ]);

      setProfile(updatedProfile);
      setLogoFile(null);
      // Apply language immediately across the whole app
      if (settings.language) setLang(settings.language);
      flash(t("cs_success"));
    } catch (err) {
      flash(t("cs_load_error"), true);
    }
    setSaving(false);
  }

  function set(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  function setP(key, value) {
    setProfile(prev => ({ ...prev, [key]: value }));
  }

  const TABS = [
    { key: "general",       label: t("cs_tab_general"),       Icon: Settings  },
    { key: "tracking",      label: t("cs_tab_tracking"),      Icon: Radio     },
    { key: "working_hours", label: t("cs_tab_hours"),         Icon: Clock     },
    { key: "alerts",        label: t("cs_tab_alerts"),        Icon: Bell      },
    { key: "appearance",    label: t("cs_tab_appearance"),    Icon: Palette   },
    { key: "client_types",  label: t("cs_tab_client_types"),  Icon: Tag       },
  ];

  if (loading) return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-gray-500 text-sm">{t("cs_loading")}</p>
    </div>
  );

  if (!settings || !profile) return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-red-500 text-sm">{t("cs_load_error")}</p>
    </div>
  );

  return (
    <div className="space-y-4">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">{t("cs_title")}</h2>
          <p className="text-gray-500 text-sm mt-1">{t("cs_subtitle")}</p>
        </div>
        <button onClick={handleSave} disabled={saving || uploadingLogo}
          className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-5 py-2 rounded-lg transition disabled:opacity-50 flex items-center gap-2">
          {(saving || uploadingLogo) && (
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"/>
              <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          )}
          {saving ? t("cs_saving") : uploadingLogo ? t("cs_uploading") : <span className="flex items-center gap-1.5"><Save className="w-4 h-4"/>{t("cs_save_changes")}</span>}
        </button>
      </div>

      {/* ── Feedback ──────────────────────────────────────────────────────── */}
      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{success}</div>}

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-2 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition ${
                activeTab === tab.key
                  ? "border-green-600 text-green-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}>
              <tab.Icon className="w-3.5 h-3.5 inline mr-1.5" />{tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* GENERAL TAB                                                      */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === "general" && (
            <div className="space-y-6">
              <Section title={t("cs_regional")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t("settings_timezone")}>
                    <select value={settings.timezone || "Asia/Dubai"} onChange={e => set("timezone", e.target.value)}
                      className={INPUT}>
                      <optgroup label="Middle East">
                        <option value="Asia/Dubai">Dubai / UAE (UTC+4)</option>
                        <option value="Asia/Riyadh">Saudi Arabia (UTC+3)</option>
                        <option value="Asia/Kuwait">Kuwait (UTC+3)</option>
                        <option value="Asia/Qatar">Qatar (UTC+3)</option>
                        <option value="Asia/Bahrain">Bahrain (UTC+3)</option>
                        <option value="Asia/Muscat">Oman (UTC+4)</option>
                        <option value="Asia/Baghdad">Iraq (UTC+3)</option>
                        <option value="Asia/Beirut">Lebanon (UTC+2/+3)</option>
                        <option value="Asia/Amman">Jordan (UTC+2/+3)</option>
                        <option value="Asia/Damascus">Syria (UTC+2/+3)</option>
                        <option value="Asia/Jerusalem">Israel / Palestine (UTC+2/+3)</option>
                        <option value="Asia/Aden">Yemen (UTC+3)</option>
                      </optgroup>
                      <optgroup label="North Africa">
                        <option value="Africa/Casablanca">Morocco (UTC+1)</option>
                        <option value="Africa/Algiers">Algeria (UTC+1)</option>
                        <option value="Africa/Tunis">Tunisia (UTC+1)</option>
                        <option value="Africa/Tripoli">Libya (UTC+2)</option>
                        <option value="Africa/Cairo">Egypt (UTC+2)</option>
                        <option value="Africa/Khartoum">Sudan (UTC+3)</option>
                      </optgroup>
                      <optgroup label="West Africa">
                        <option value="Africa/Dakar">Senegal (UTC+0)</option>
                        <option value="Africa/Abidjan">Ivory Coast (UTC+0)</option>
                        <option value="Africa/Accra">Ghana (UTC+0)</option>
                        <option value="Africa/Lagos">Nigeria (UTC+1)</option>
                        <option value="Africa/Bamako">Mali (UTC+0)</option>
                        <option value="Africa/Conakry">Guinea (UTC+0)</option>
                        <option value="Africa/Douala">Cameroon (UTC+1)</option>
                      </optgroup>
                      <optgroup label="East Africa">
                        <option value="Africa/Nairobi">Kenya / East Africa (UTC+3)</option>
                        <option value="Africa/Addis_Ababa">Ethiopia (UTC+3)</option>
                        <option value="Africa/Dar_es_Salaam">Tanzania (UTC+3)</option>
                        <option value="Africa/Kampala">Uganda (UTC+3)</option>
                      </optgroup>
                      <optgroup label="Southern Africa">
                        <option value="Africa/Johannesburg">South Africa (UTC+2)</option>
                        <option value="Africa/Lusaka">Zambia (UTC+2)</option>
                        <option value="Africa/Harare">Zimbabwe (UTC+2)</option>
                      </optgroup>
                      <optgroup label="Europe">
                        <option value="Europe/London">UK / Ireland (UTC+0/+1)</option>
                        <option value="Europe/Paris">France / Central Europe (UTC+1/+2)</option>
                        <option value="Europe/Berlin">Germany (UTC+1/+2)</option>
                        <option value="Europe/Madrid">Spain (UTC+1/+2)</option>
                        <option value="Europe/Rome">Italy (UTC+1/+2)</option>
                        <option value="Europe/Amsterdam">Netherlands (UTC+1/+2)</option>
                        <option value="Europe/Brussels">Belgium (UTC+1/+2)</option>
                        <option value="Europe/Zurich">Switzerland (UTC+1/+2)</option>
                        <option value="Europe/Stockholm">Sweden (UTC+1/+2)</option>
                        <option value="Europe/Istanbul">Turkey (UTC+3)</option>
                        <option value="Europe/Moscow">Russia / Moscow (UTC+3)</option>
                      </optgroup>
                      <optgroup label="Asia">
                        <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                        <option value="Asia/Kolkata">India (UTC+5:30)</option>
                        <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                        <option value="Asia/Colombo">Sri Lanka (UTC+5:30)</option>
                        <option value="Asia/Bangkok">Thailand (UTC+7)</option>
                        <option value="Asia/Singapore">Singapore / Malaysia (UTC+8)</option>
                        <option value="Asia/Shanghai">China (UTC+8)</option>
                        <option value="Asia/Tokyo">Japan (UTC+9)</option>
                        <option value="Asia/Seoul">South Korea (UTC+9)</option>
                      </optgroup>
                      <optgroup label="Americas">
                        <option value="America/New_York">US Eastern (UTC-5/-4)</option>
                        <option value="America/Chicago">US Central (UTC-6/-5)</option>
                        <option value="America/Denver">US Mountain (UTC-7/-6)</option>
                        <option value="America/Los_Angeles">US Pacific (UTC-8/-7)</option>
                        <option value="America/Sao_Paulo">Brazil (UTC-3)</option>
                        <option value="America/Mexico_City">Mexico (UTC-6/-5)</option>
                      </optgroup>
                      <optgroup label="Universal">
                        <option value="UTC">UTC (UTC+0)</option>
                      </optgroup>
                    </select>
                  </Field>
                  <Field label={t("settings_language")}>
                    <select value={settings.language || "en"} onChange={e => set("language", e.target.value)}
                      className={INPUT}>
                      <option value="en">🇬🇧 English</option>
                      <option value="ar">🇦🇪 Arabic</option>
                      <option value="fr">🇫🇷 French</option>
                    </select>
                  </Field>
                  <Field label={t("settings_date_format")}>
                    <select value={settings.date_format || "DD/MM/YYYY"} onChange={e => set("date_format", e.target.value)}
                      className={INPUT}>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </Field>
                </div>
              </Section>

              <Section title={t("cs_behaviour")}>
                <div className="space-y-3">
                  <ToggleRow
                    label={t("cs_auto_checkin")}
                    desc={t("cs_auto_checkin_desc")}
                    value={settings.auto_checkin}
                    onChange={v => set("auto_checkin", v)}
                  />
                  <ToggleRow
                    label={t("cs_manual_checkin")}
                    desc={t("cs_manual_checkin_desc")}
                    value={settings.allow_manual_checkin}
                    onChange={v => set("allow_manual_checkin", v)}
                  />
                  <ToggleRow
                    label={t("cs_require_notes")}
                    desc={t("cs_require_notes_desc")}
                    value={settings.require_notes_on_visit}
                    onChange={v => set("require_notes_on_visit", v)}
                  />
                  <ToggleRow
                    label={t("cs_notif_sound")}
                    desc={t("cs_notif_sound_desc")}
                    value={settings.notification_sound}
                    onChange={v => set("notification_sound", v)}
                  />
                </div>
              </Section>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* TRACKING TAB                                                     */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === "tracking" && (
            <div className="space-y-6">
              <Section title={t("cs_gps_tracking")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t("cs_nearby_distance")}
                    hint={t("cs_nearby_hint")}>
                    <input type="number" value={settings.nearby_distance_meters || 30}
                      onChange={e => set("nearby_distance_meters", parseInt(e.target.value) || 30)}
                      className={INPUT} min="5" max="500" />
                  </Field>
                  <Field label={t("cs_gps_interval")}
                    hint={t("cs_gps_interval_hint")}>
                    <input type="number" value={settings.gps_interval_seconds || 30}
                      onChange={e => set("gps_interval_seconds", parseInt(e.target.value) || 30)}
                      className={INPUT} min="5" max="300" />
                  </Field>
                  <Field label={t("cs_min_arrival")}
                    hint={t("cs_nearby_hint")}>
                    <input type="number" value={settings.nearby_min ?? 20}
                      onChange={e => set("nearby_min", parseInt(e.target.value) || 0)}
                      className={INPUT} min="0" max="500" />
                  </Field>
                  <Field label={t("cs_max_arrival")}
                    hint={t("cs_nearby_hint")}>
                    <input type="number" value={settings.nearby_max ?? 100}
                      onChange={e => set("nearby_max", parseInt(e.target.value) || 100)}
                      className={INPUT} min="1" max="5000" />
                  </Field>
                  <Field label={t("cs_visit_reset")}
                    hint={t("cs_visit_reset_hint")}>
                    <input type="number" min="1" max="365"
                      value={settings.visit_reset_days ?? 1}
                      onChange={e => set("visit_reset_days", Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                      className={INPUT} />
                  </Field>
                </div>
              </Section>

              <Section title={t("cs_max_speed")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t("cs_max_speed")}
                    hint={t("cs_max_speed_hint")}>
                    <input type="number" value={settings.max_speed_kmh || 0}
                      onChange={e => set("max_speed_kmh", parseInt(e.target.value) || 0)}
                      className={INPUT} min="0" max="250" />
                  </Field>
                </div>
              </Section>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* WORKING HOURS TAB                                                */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === "working_hours" && (
            <div className="space-y-6">
              <Section title={t("cs_working_hours")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t("cs_hours_start")}>
                    <input type="time" value={settings.working_hours_start || "08:00"}
                      onChange={e => set("working_hours_start", e.target.value)}
                      className={INPUT} />
                  </Field>
                  <Field label={t("cs_hours_end")}>
                    <input type="time" value={settings.working_hours_end || "23:00"}
                      onChange={e => set("working_hours_end", e.target.value)}
                      className={INPUT} />
                  </Field>
                </div>
                <div className="mt-3">
                  <ToggleRow
                    label={t("cs_track_outside")}
                    desc={t("cs_track_outside_desc")}
                    value={settings.track_outside_hours}
                    onChange={v => set("track_outside_hours", v)}
                  />
                </div>
              </Section>

              <Section title={t("settings_working_days")}>
                <p className="text-xs text-gray-500 mb-3">{t("settings_working_days_hint")}</p>
                <div className="flex gap-2 flex-wrap">
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((day, i) => {
                    const days = settings.working_days || [0,1,2,3,4];
                    const active = days.includes(i);
                    return (
                      <button key={day} type="button"
                        onClick={() => {
                          const updated = active
                            ? days.filter(d => d !== i)
                            : [...days, i].sort();
                          set("working_days", updated);
                        }}
                        className={`w-14 py-2 rounded-lg text-sm font-medium border transition ${
                          active
                            ? "bg-green-600 text-white border-green-600"
                            : "bg-white text-gray-500 border-gray-300 hover:border-green-400"
                        }`}>
                        {day}
                      </button>
                    );
                  })}
                </div>
              </Section>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* ALERTS TAB                                                       */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === "alerts" && (
            <div className="space-y-6">
              <Section title={t("cs_out_of_zone")}>
                <div className="space-y-3">
                  <ToggleRow
                    label={t("cs_out_of_zone")}
                    desc={t("cs_out_of_zone_desc")}
                    value={settings.out_of_zone_alerts}
                    onChange={v => set("out_of_zone_alerts", v)}
                  />
                  <ToggleRow
                    label={t("cs_unauth_zone")}
                    desc={t("cs_unauth_zone_desc")}
                    value={settings.unauthorized_zone_alerts !== false}
                    onChange={v => set("unauthorized_zone_alerts", v)}
                  />
                </div>
                <div className="mt-4">
                  <Field label={t("cs_cooldown")}
                    hint={t("cs_cooldown_hint")}>
                    <input type="number" value={settings.alert_cooldown_minutes || 15}
                      onChange={e => set("alert_cooldown_minutes", parseInt(e.target.value) || 15)}
                      className={INPUT} min="1" max="120" />
                  </Field>
                </div>
              </Section>

              <Section title={t("cs_alerts_section")}>
                <div className="space-y-3">
                  <ToggleRow
                    label={t("cs_notif_checkin")}
                    desc={t("cs_notif_checkin_desc")}
                    value={settings.notify_checkins !== false}
                    onChange={v => set("notify_checkins", v)}
                  />
                </div>
              </Section>

              <Section title={t("cs_visit_alerts")}>
                <div className="space-y-3">
                  <ToggleRow
                    label={t("cs_missed_visit")}
                    desc={t("cs_missed_visit_desc")}
                    value={settings.alert_missed_visit}
                    onChange={v => set("alert_missed_visit", v)}
                  />
                  <ToggleRow
                    label={t("cs_long_stop")}
                    desc={t("cs_long_stop_desc")}
                    value={settings.alert_long_stop}
                    onChange={v => set("alert_long_stop", v)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("cs_long_stop_min")}</label>
                    <input
                      type="number"
                      min={5}
                      max={480}
                      value={settings.long_stop_minutes ?? 30}
                      onChange={e => setSettings(p => ({ ...p, long_stop_minutes: parseInt(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t("cs_long_stop_hint")}</p>
                  </div>
                </div>
              </Section>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* APPEARANCE TAB                                                   */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === "appearance" && (
            <div className="space-y-6">
              <Section title={t("cs_appearance")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label={t("cs_company_name")}>
                    <input value={profile.name || ""}
                      onChange={e => setP("name", e.target.value)}
                      className={INPUT} placeholder={t("cs_company_name")} />
                  </Field>
                  <Field label={t("cs_company_phone")}>
                    <input value={profile.phone || ""}
                      onChange={e => setP("phone", e.target.value)}
                      className={INPUT} placeholder="+971 50 000 0000" />
                  </Field>
                  <Field label={t("cs_company_email")}>
                    <input type="email" value={profile.email || ""}
                      onChange={e => setP("email", e.target.value)}
                      className={INPUT} placeholder="company@email.com" />
                  </Field>
                  <Field label={t("cs_company_address")}>
                    <input value={profile.address || ""}
                      onChange={e => setP("address", e.target.value)}
                      className={INPUT} placeholder={t("cs_company_address")} />
                  </Field>
                </div>
              </Section>

              <Section title={t("cs_appearance")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  {/* Logo */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">{t("cs_logo")}</p>
                    <div className="flex items-center gap-4">
                      {/* Preview */}
                      <div className="w-20 h-20 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                        {(logoPreview || profile.logo_url) ? (
                          <img src={logoPreview || profile.logo_url} alt="Logo"
                            className="w-full h-full object-contain" />
                        ) : (
                          <Building2 className="w-8 h-8 text-gray-300" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                          onChange={handleLogoSelect} />
                        <button type="button" onClick={() => logoInputRef.current.click()}
                          className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg font-medium block">
                          {profile.logo_url ? t("cs_choose_logo") : t("cs_choose_logo")}
                        </button>
                        {logoPreview && (
                          <button type="button"
                            onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                            className="text-xs text-red-500 hover:text-red-700">
                            {t("cancel")}
                          </button>
                        )}
                        <p className="text-xs text-gray-400">{t("cs_logo_hint")}</p>
                      </div>
                    </div>
                  </div>

                  {/* Primary Color */}
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">{t("cs_primary_color")}</p>
                    <p className="text-xs text-gray-400 mb-3">{t("settings_color_hint")}</p>
                    <div className="flex items-center gap-3">
                      <input type="color" value={profile.primary_color || "#16a34a"}
                        onChange={e => setP("primary_color", e.target.value)}
                        className="w-12 h-10 border border-gray-300 rounded-lg px-1 py-1 cursor-pointer" />
                      <input value={profile.primary_color || "#16a34a"}
                        onChange={e => setP("primary_color", e.target.value)}
                        className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="#16a34a" maxLength={7} />
                      <div className="w-8 h-8 rounded-lg border border-gray-200"
                        style={{ backgroundColor: profile.primary_color || "#16a34a" }} />
                    </div>

                    {/* Color presets */}
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {["#16a34a","#2563eb","#9333ea","#dc2626","#d97706","#0891b2","#374151"].map(color => (
                        <button key={color} type="button"
                          onClick={() => setP("primary_color", color)}
                          style={{ backgroundColor: color }}
                          className={`w-7 h-7 rounded-full transition ${
                            profile.primary_color === color ? "ring-2 ring-offset-2 ring-gray-400" : ""
                          }`} />
                      ))}
                    </div>
                  </div>
                </div>
              </Section>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════ */}
          {/* CLIENT TYPES TAB                                                  */}
          {/* ════════════════════════════════════════════════════════════════ */}
          {activeTab === "client_types" && (
            <div className="space-y-6">
              <Section title={t("cs_tab_client_types")}>
                <p className="text-xs text-gray-500 mb-4">
                  {t("cs_client_types_hint")}
                </p>

                {/* Existing types */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {(settings.client_types || []).length === 0 && (
                    <p className="text-sm text-gray-400">{t("cs_no_types")}</p>
                  )}
                  {(settings.client_types || []).map((type, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-3 py-1.5 text-sm font-medium">
                      <Tag className="w-3 h-3 text-gray-400" />
                      <span>{type}</span>
                      <button
                        type="button"
                        onClick={() => set("client_types", (settings.client_types || []).filter((_, j) => j !== i))}
                        className="ml-1 text-gray-400 hover:text-red-500 transition">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new type */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTypeName}
                    onChange={e => setNewTypeName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key !== "Enter") return;
                      const v = newTypeName.trim();
                      if (!v) return;
                      const existing = settings.client_types || [];
                      if (existing.includes(v)) return;
                      set("client_types", [...existing, v]);
                      setNewTypeName("");
                    }}
                    placeholder={t("cs_new_type_ph")}
                    className={INPUT}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const v = newTypeName.trim();
                      if (!v) return;
                      const existing = settings.client_types || [];
                      if (existing.includes(v)) return;
                      set("client_types", [...existing, v]);
                      setNewTypeName("");
                    }}
                    className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg whitespace-nowrap">
                    <Plus className="w-4 h-4" /> {t("cs_add_type")}
                  </button>
                </div>
              </Section>
            </div>
          )}

        </div>
      </div>

      {/* Save button at bottom too */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex justify-end">
        <button onClick={handleSave} disabled={saving || uploadingLogo}
          className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition disabled:opacity-50">
          {saving ? t("cs_saving") : <span className="flex items-center gap-1.5"><Save className="w-4 h-4"/>{t("cs_save_changes")}</span>}
        </button>
      </div>

    </div>
  );
}

// ── Reusable layout helpers ────────────────────────────────────────────────────
const INPUT = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500";

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-gray-400 text-xs mt-1">{hint}</p>}
    </div>
  );
}

function ToggleRow({ label, desc, value, onChange }) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100">
      <div className="flex-1 mr-4">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {desc && <p className="text-gray-400 text-xs mt-0.5">{desc}</p>}
      </div>
      <UISwitch value={!!value} onChange={onChange} size="sm" />
    </div>
  );
}

export default CompanySettings;
